import {
  CompanyEntity, firstOrUndefined,
  FlowAllocationType,
  FreightTypeEntity,
  getObjectId,
  getRequiredObjectId,
  InvoiceGroupingDefault,
  InvoiceGroupingDefaultEntity,
  RateModelEntity,
  SiteEntity,
  TimeGroup, toFlowAllocationType, toTimeGroup, toTransportCategory,
  TransportCategory,
  useInvoiceGroupingDefaults
} from "@ots-frontend/ots-data";
import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {ActionIcon, Button, Card, Divider, Grid, Group, Radio, Switch, TextInput, Title} from "@mantine/core";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useDisclosure} from "@mantine/hooks";
import {
  InvoiceGroupingDefaultFormModal,
  InvoiceGroupingDefaultFormValues
} from "../InvoiceGroupingDefaultForm/InvoiceGroupingDefaultForm";
import {
  InvoiceGroupingDefaultDataTable
} from "../../data-table/InvoiceGroupingDefaultDataTable/InvoiceGroupingDefaultDataTable";
import {DateInput} from "../../date/DateInput/DateInput";
import {RateModelSelect} from "../../select/entity/RateModelSelect/RateModelSelect";
import {OperatorCompanySelect} from "../../select/entity/CompanySelect/OperatorCompanySelect";
import {VendorCompanySelect} from "../../select/entity/CompanySelect/VendorCompanySelect";
import {SiteMultiSelect} from "../../select/entity/SiteSelect/SiteMultiSelect";
import {TruckCompanyMultiSelect} from "../../select/entity/CompanySelect/TruckCompanyMultiSelect";
import {PipelineMultiSelect} from "../../select/entity/PipelineSelect/PipelineMultiSelect";
import {TimeGroupSelect} from "../../select/entity/TimeGroupSelect/TimeGroupSelect";
import {FlowAllocationTypeSelect} from "../../select/entity/FlowAllocationTypeSelect/FlowAllocationTypeSelect";
import {FreightTypeMultiSelect} from "../../select/entity/FreightTypeSelect/FreightTypeMultiSelect";
import {IconTrash} from "@tabler/icons-react";

interface CompanyDisposalReportFormProps {
  options?: CompanyDisposalReportFormOptions;
  defaultValues?: Partial<CompanyDisposalReportFormValues>;
  onSubmit?: (values: CompanyDisposalReportFormValues) => void;
  submitting?: boolean;
}

export interface CompanyDisposalReportFormOptions {
  regionId?: string
  category?: TransportCategory
}

export interface CompanyDisposalReportFormValues {
  category?: TransportCategory
  fromDate: Date;
  toDate: Date;
  operator: CompanyEntity | string;
  owner?: CompanyEntity | string
  disposalCompany: CompanyEntity | string;
  truckCompanies?: (CompanyEntity | string)[];
  rateModel: RateModelEntity | string;
  freightTypeGroups?: (FreightTypeEntity | string)[][];
  sites?: (SiteEntity | string)[];
  includeTruckingData?: boolean;
  includePipelineData?: boolean;
  pipelineFlowTimeGroup?: TimeGroup;
  pipelineFlowAllocationType?: FlowAllocationType;
  sortByFreightType?: boolean;
  breakdownBy: 'pickup' | 'delivery';
  createInvoices?: boolean;
  invoiceNumber?: string;
  invoiceGroupings?: InvoiceGroupingDefaultEntity[];
}

const getDefaultValues = (defaultValues: Partial<CompanyDisposalReportFormValues> | undefined) => {
  return {
    category: defaultValues?.category?.code,
    fromDate: defaultValues?.fromDate,
    toDate: defaultValues?.toDate,
    operator: getObjectId(defaultValues?.operator),
    owner: getObjectId(defaultValues?.owner),
    disposalCompany: getObjectId(defaultValues?.disposalCompany),
    truckCompanies: defaultValues?.truckCompanies?.filter((item): item is (string | CompanyEntity) => !!item).map(c => getRequiredObjectId(c)),
    rateModel: getObjectId(defaultValues?.rateModel),
    freightTypeGroups: defaultValues?.freightTypeGroups?.map((freightTypes) => ({
      values: freightTypes.map(getRequiredObjectId)
    })) ?? [],
    sites: defaultValues?.sites?.filter((item): item is (string | SiteEntity) => !!item).map(c => getRequiredObjectId(c)),
    includeTruckingData: defaultValues?.includeTruckingData ?? true,
    includePipelineData: defaultValues?.includePipelineData ?? true,
    pipelineFlowTimeGroup: defaultValues?.pipelineFlowTimeGroup?.code,
    pipelineFlowAllocationType: defaultValues?.pipelineFlowAllocationType?.code,
    sortByFreightType: defaultValues?.sortByFreightType ?? true,
    breakdownBy: defaultValues?.breakdownBy ?? 'pickup',
    createInvoices: defaultValues?.createInvoices,
    invoiceNumber: defaultValues?.invoiceNumber,
    invoiceGroupings: defaultValues?.invoiceGroupings ?? [],
  }
}

export const CompanyDisposalReportForm = (props: CompanyDisposalReportFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["common"]);

  const [opened, handlers] = useDisclosure(false);

  const form = useForm({
    initialValues: getDefaultValues(defaultValues),
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        category: values.category ? toTransportCategory(values.category) : undefined,
        fromDate: values.fromDate!,
        toDate: values.toDate!,
        operator: values.operator!,
        owner: values.owner,
        disposalCompany: values.disposalCompany!,
        truckCompanies: values.truckCompanies,
        rateModel: values.rateModel!,
        freightTypeGroups: values.freightTypeGroups.map((group) => [...group.values]),
        sites: values.sites,
        includeTruckingData: values.includeTruckingData,
        includePipelineData: values.includePipelineData,
        pipelineFlowTimeGroup: values.pipelineFlowTimeGroup ? toTimeGroup(values.pipelineFlowTimeGroup) : undefined,
        pipelineFlowAllocationType: values.pipelineFlowAllocationType ? toFlowAllocationType(values.pipelineFlowAllocationType) : undefined,
        sortByFreightType: values.sortByFreightType,
        breakdownBy: values.breakdownBy,
        createInvoices: values.createInvoices,
        invoiceNumber: values.invoiceNumber,
        invoiceGroupings: values.invoiceGroupings,
      })
    }
  }, [form, onSubmit]);

  const onAddInvoiceGroupDefault = useCallback((formValues: InvoiceGroupingDefaultFormValues) => {
    const invoiceGroupingDefault: InvoiceGroupingDefault = {
      id: '',
      companyId: '',
      regionId: '',
      rateModelId: getRequiredObjectId(formValues.rateModel),
      type: formValues.type,
      objectId: getObjectId(formValues.freightType) ?? getObjectId(formValues.serviceType),
      invoiceGroupBy: formValues.invoiceGroupBy,
      invoiceMaxCost: formValues.invoiceMaxCost,
      invoiceMaxLineItem: formValues.invoiceMaxLineItem,
    }

    form.insertListItem('invoiceGroupings', invoiceGroupingDefault)

    handlers.close()
  }, [form, handlers])

  const onRemoveInvoiceGroupDefault = useCallback((record: InvoiceGroupingDefaultEntity) => {
    const indexes: number[] = []

    form.values.invoiceGroupings.forEach((igb, index) => {
      if (igb.id === record.id) {
        indexes.push(index)
      }
    })

    indexes.forEach((index) => {
      form.removeListItem('invoiceGroupings', index)
    })
  }, [form])

  const [selectedOperator, setSelectedOperator] = useState<CompanyEntity | undefined>();
  const [selectedOwner, setSelectedOwner] = useState<CompanyEntity | undefined>();
  const [selectedRateModel, setSelectedRateModel] = useState<RateModelEntity | undefined>();

  const {invoiceGroupings: defaultInvoiceGroupings} = useInvoiceGroupingDefaults({
    operator: selectedOwner ?? selectedOperator,
    truckCompany: undefined,
    rateModel: selectedRateModel
  })

  useEffect(() => {
    if (defaultInvoiceGroupings) {
      defaultInvoiceGroupings.forEach((value, index) => {
        form.insertListItem('invoiceGroupings', value)
      })
    }
  }, [defaultInvoiceGroupings]);

  const invoiceGroupings = useMemo(() => {
    return form.values.invoiceGroupings.map((v) => InvoiceGroupingDefaultEntity.from(v))
  }, [form.values.invoiceGroupings])

  const freightTypeFields = form.values.freightTypeGroups.map((item, index) => (
    <Group key={`freight_type_group_${index}`} mt="xs">
      <FreightTypeMultiSelect
        label={t("common.report.companydisposal.form.field.freighttypegroup", "Freight Type Group") + " " + (index + 1)}
        placeholder={t<string>("common.report.companydisposal.form.field.freighttypegroup", "Freight Type Group") + " " + (index + 1)}
        clearable={true}
        withinPortal={true}
        sx={{ flex: 1 }}
        {...form.getInputProps(`freightTypeGroups.${index}.values`)}
      />
      <ActionIcon color="red" onClick={() => form.removeListItem('freightTypeGroups', index)}>
        <IconTrash size="1rem" />
      </ActionIcon>
    </Group>
  ));


  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
        <Grid>
          <Grid.Col span={6}>
            <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
              <Grid>
                <Grid.Col span={6}>
                  <DateInput
                    valueFormat="MM/DD/YYYY HH:mm"
                    label={t("common.report.companydisposal.form.field.fromdate", "From Date")}
                    placeholder={t<string>("common.report.companydisposal.form.field.fromdate", "From Date")}
                    clearable
                    withAsterisk
                    popoverProps={{
                      withinPortal: true
                    }}
                    required
                    {...form.getInputProps("fromDate")} />
                </Grid.Col>
                <Grid.Col span={6}>
                  <DateInput
                    miw={100}
                    valueFormat="MM/DD/YYYY HH:mm"
                    label={t("common.report.companydisposal.form.field.todate", "To Date")}
                    placeholder={t<string>("common.report.companydisposal.form.field.todate", "To Date")}
                    clearable
                    withAsterisk
                    popoverProps={{
                      withinPortal: true
                    }}
                    required
                    {...form.getInputProps("toDate")} />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Divider my="sm" variant="dotted"/>
                </Grid.Col>
                <Grid.Col span={12}>
                  <OperatorCompanySelect
                    label={t("common.report.companydisposal.form.field.operator", "Operator")}
                    placeholder={t<string>("common.report.companydisposal.form.field.operator", "Operator")}
                    styles={{wrapper: {width: "50%"}}}
                    withinPortal={true}
                    selector={{
                      regionId: options?.regionId,
                    }}
                    onEntityChange={(entities) => setSelectedOperator(firstOrUndefined(entities))}
                    withAsterisk
                    required
                    clearable
                    {...form.getInputProps("operator")} />
                </Grid.Col>
                <Grid.Col span={12}>
                  <OperatorCompanySelect
                    label={t("common.report.companydisposal.form.field.owner", "Owner")}
                    placeholder={t<string>("common.report.companydisposal.form.field.owner", "Owner")}
                    styles={{wrapper: {width: "50%"}}}
                    withinPortal={true}
                    selector={{
                      regionId: options?.regionId,
                    }}
                    onEntityChange={(entities) => setSelectedOwner(firstOrUndefined(entities))}
                    clearable
                    {...form.getInputProps("owner")} />
                </Grid.Col>
                <Grid.Col span={12}>
                  <VendorCompanySelect
                    label={t("common.report.companydisposal.form.field.vendor", "Vendor")}
                    placeholder={t<string>("common.report.companydisposal.form.field.vendor", "Vendor")}
                    styles={{wrapper: {width: "50%"}}}
                    withinPortal={true}
                    selector={{
                      regionId: options?.regionId,
                    }}
                    withAsterisk
                    required
                    clearable
                    {...form.getInputProps("disposalCompany")} />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Divider my="sm" variant="dotted"/>
                </Grid.Col>
                <Grid.Col span={12}>
                  <RateModelSelect
                    label={t("common.report.companydisposal.form.field.ratemodel", "Rate Model")}
                    placeholder={t<string>("common.report.companydisposal.form.field.ratemodel", "Rate Model")}
                    styles={{wrapper: {width: "50%"}}}
                    withinPortal={true}
                    selector={{
                      regionId: options?.regionId,
                    }}
                    onEntityChange={(entities) => setSelectedRateModel(firstOrUndefined(entities))}
                    withAsterisk
                    required
                    clearable
                    {...form.getInputProps("rateModel")} />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Divider my="sm" variant="dotted"/>
                </Grid.Col>
                <Grid.Col span={12}>
                  <SiteMultiSelect
                    label={t("common.report.companydisposal.form.field.sites", "Sites")}
                    placeholder={t<string>("common.report.companydisposal.form.field.sites", "Sites")}
                    styles={{wrapper: {width: "75%"}}}
                    withinPortal={true}
                    selector={{
                      regionId: options?.regionId,
                    }}
                    clearable
                    {...form.getInputProps("sites")} />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Divider my="sm" variant="dotted"/>
                </Grid.Col>
                <Grid.Col span={12}>
                  <Switch
                    label={t("common.report.companydisposal.form.field.includetruckingdata", "Include Trucking Data")}
                    placeholder={t<string>("common.report.companydisposal.form.field.includetruckingdata", "Include Trucking Data")}
                    {...form.getInputProps("includeTruckingData", {type: 'checkbox'})} />
                </Grid.Col>
                <Grid.Col span={12}>
                  <TruckCompanyMultiSelect
                    label={t("common.report.companydisposal.form.field.truckcompany", "Truck Companies")}
                    placeholder={t<string>("common.report.companydisposal.form.field.truckcompany", "Truck Companies")}
                    styles={{wrapper: {width: "75%"}}}
                    withinPortal={true}
                    selector={{
                      regionId: options?.regionId,
                    }}
                    clearable
                    {...form.getInputProps("truckCompanies")} />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Divider my="sm" variant="dotted"/>
                </Grid.Col>
                <Grid.Col span={12}>
                  <Switch
                    label={t("common.report.companydisposal.form.field.includepipelinedata", "Include Pipeline Data")}
                    placeholder={t<string>("common.report.companydisposal.form.field.includepipelinedata", "Include Pipeline Data")}
                    {...form.getInputProps("includePipelineData", {type: 'checkbox'})} />
                </Grid.Col>
                <Grid.Col span={12}>
                  <PipelineMultiSelect
                    label={t("common.report.companydisposal.form.field.pipeline", "Pipelines")}
                    placeholder={t<string>("common.report.companydisposal.form.field.pipeline", "Pipelines")}
                    styles={{wrapper: {width: "75%"}}}
                    withinPortal={true}
                    selector={{
                      regionId: options?.regionId,
                    }}
                    clearable
                    {...form.getInputProps("pipelines")} />
                  <TimeGroupSelect
                    label={t("common.report.companydisposal.form.field.timegroup", "Time Group")}
                    placeholder={t<string>("common.report.companydisposal.form.field.timegroup", "Time Group")}
                    styles={{wrapper: {width: "50%"}}}
                    withinPortal={true}
                    clearable
                    {...form.getInputProps("pipelineFlowTimeGroup")} />
                  <FlowAllocationTypeSelect
                    label={t("common.report.companydisposal.form.field.allocation", "Allocation")}
                    placeholder={t<string>("common.report.companydisposal.form.field.allocation", "Allocation")}
                    styles={{wrapper: {width: "50%"}}}
                    withinPortal={true}
                    clearable
                    {...form.getInputProps("pipelineFlowAllocationType")} />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Divider my="sm" variant="dotted"/>
                </Grid.Col>
                <Grid.Col span={12}>
                  <Switch
                    label={t("common.report.companydisposal.form.field.sortbyfreighttype", "Sort By Freight Type")}
                    placeholder={t<string>("common.report.companydisposal.form.field.sortbyfreighttype", "Sort By Freight Type")}
                    {...form.getInputProps("sortByFreightType", {type: 'checkbox'})} />
                </Grid.Col>
                <Grid.Col span={12}>
                  <Radio.Group
                    label={t("common.report.companydisposal.form.field.breakdown", "Breakdown By")}
                    {...form.getInputProps("breakdownBy")}
                  >
                    <Group mt="xs">
                      <Radio value="pickup" label="Pickup"/>
                      <Radio value="delivery" label="Delivery"/>
                    </Group>
                  </Radio.Group>
                </Grid.Col>
              </Grid>
            </Card>
          </Grid.Col>
          <Grid.Col span={6}>
            <Grid>
              <Grid.Col span={12}>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Title order={2} pb="md">
                    Freight Types
                  </Title>
                  <Grid>
                    {freightTypeFields &&
                      <Grid.Col span={12}>
                        {freightTypeFields}
                      </Grid.Col>
                    }
                    <Grid.Col span={12}>
                      <Button
                        onClick={() =>
                          form.insertListItem('freightTypeGroups', { values: [] })
                        }
                      >
                        Add Freight Type Group
                      </Button>
                    </Grid.Col>
                  </Grid>
                </Card>
              </Grid.Col>
              <Grid.Col span={12}>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Title order={2} pb="md">
                    Invoice Configuration
                  </Title>
                  <Switch
                    mb="sm"
                    label={t("common.report.companydisposal.form.field.createinvoice", "Create Invoices")}
                    {...form.getInputProps("createInvoices", {type: 'checkbox'})} />
                  {form.values.createInvoices &&
                    <>
                      <TextInput
                        mb="lg"
                        label={t("common.report.companydisposal.form.field.invoicenumber", "Invoice Number")}
                        placeholder={t<string>("common.report.companydisposal.form.field.invoicenumber", "Invoice Number")}
                        styles={{wrapper: {width: "50%"}}}
                        {...form.getInputProps("invoiceNumber")} />
                      <Divider my="sm" variant="dotted"/>
                      <Title order={6} mt="lg" mb="md">
                        Invoice Groupings
                      </Title>
                      <InvoiceGroupingDefaultDataTable
                        withBorder={true}
                        mb="lg"
                        records={invoiceGroupings}
                        onDelete={onRemoveInvoiceGroupDefault}/>
                      <Button variant="outline" onClick={() => handlers.open()}>
                        {t("common.action.add", "Add", {ns: "common"})}
                      </Button>
                    </>
                  }
                </Card>
              </Grid.Col>
            </Grid>
          </Grid.Col>
          <Grid.Col span={12}>
            <Button type="submit" loading={submitting} fullWidth mt="xl">
              {t("common.action.submit", {ns: "common"})}
            </Button>
          </Grid.Col>
        </Grid>
      </Card>

      <InvoiceGroupingDefaultFormModal
        size="lg"
        opened={opened}
        onClose={handlers.close}
        onSubmit={onAddInvoiceGroupDefault}
        options={{
          regionId: options?.regionId
        }}/>


    </form>
  );
};
